import { registerComponent, useMatchLargeViewport, withProgressiveHydration } from '@avensia/excite';
import { styled } from '@glitz/react';
import { theme } from 'nordicnest/shared/theme';
import * as React from 'react';
import { Carousel, useCarousel } from 'webapp/components/Carousel';
import Link from 'webapp/components/Link';
import { ButtonLink } from 'webapp/components/button/button';
import { Block, edgeMarginsDecorator, maxContentNormalWidthDecorator } from 'webapp/components/page-layout';
import { isSvenssons, SiteTheme, Theme } from 'webapp/theme';
import { createButtonHoverDecorator } from 'webapp/utils/button-hover';
import { createMaxSmallViewportDecorator, createMinLargeViewportDecorator } from 'webapp/viewport';
import { linkWithoutQueryParams } from 'webapp/link-url-decorator';

const LinkListContext = React.createContext({});

export function LinkListProvider({ children, ...props }: React.PropsWithChildren) {
  return <LinkListContext.Provider value={props}>{children}</LinkListContext.Provider>;
}

export default registerComponent<NordicNest.Api.LinkListBlockApiModel>(withProgressiveHydration(LinkListBlock));
function LinkListBlock(props: NordicNest.Api.LinkListBlockApiModel) {
  return (
    <Base
      css={styled(
        !props.wrapLinks && buttonNoWrapDecorator,
        props.topPadding !== false && { paddingTop: t => t.sharedSpacing.m },
        props.bottomPadding !== false && { paddingBottom: t => t.sharedSpacing.m },
      )}
    >
      {props.heading && <Heading>{props.heading}</Heading>}
      <LinkList {...props} />
    </Base>
  );
}

export const LinkList = styled(
  (props: {
    links: Excite.ContentApi.LinkApiModel[];
    backgroundColor?: string;
    borderColor?: string;
    textColor?: string;
    centerLinks?: boolean;
    iconLinks?: NordicNest.Api.CFIconLinkApiModel[];
    wrapLinks?: boolean;
  }) => {
    const isLargeViewport = useMatchLargeViewport();
    const hasIconLinks = props.iconLinks && props.iconLinks.length > 0;
    const containerDecorator = styled(props.centerLinks && props.wrapLinks && centerButtonsDecorator, {
      display: 'flex',
    });

    const buttonDecorator = React.useMemo(
      () =>
        styled(
          createButtonHoverDecorator({
            backgroundColor: props.backgroundColor ? props.backgroundColor : t => t.transparent.color,
            color: props.textColor ? props.textColor : t => t.transparent.text,
            border: { xy: { color: t => (props.borderColor ? props.borderColor : t.monochrome.color) } },
          }),
          {
            backgroundColor: props.backgroundColor ? props.backgroundColor : t => t.transparent.color,
            color: props.textColor ? props.textColor : t => t.transparent.text,
            border: { xy: { color: t => (props.borderColor ? props.borderColor : t.monochrome.color) } },
          },
        ),
      [props.backgroundColor, props.borderColor, props.textColor],
    );

    const alignButtonsDecorator = React.useMemo(
      () =>
        styled(
          props.iconLinks && props.iconLinks.length > 0 && iconListDecorator,
          props.centerLinks && props.wrapLinks && centerButtonsDecorator,
        ),
      [props.centerLinks, props.iconLinks, props.wrapLinks],
    );

    const carousel = useCarousel(undefined, {
      loop: false,
      dragFree: true,
      align: 'start',
    });

    const buttons = props.links.map(link => (
      <Button
        square={!isSvenssons}
        css={styled(
          isSvenssons && buttonSizeDecorator,
          props.backgroundColor || props.textColor ? buttonDecorator : undefined,
        )}
        as={isSvenssons ? Theme.Monochrome : Theme.White}
        key={link.to}
        {...linkWithoutQueryParams(link)}
      />
    ));

    return hasIconLinks || (props.wrapLinks && isLargeViewport) ? (
      <Buttons css={alignButtonsDecorator}>
        {buttons}
        {props.iconLinks?.map((link, index) => {
          const { children, ...restLink } = link.link;
          return (
            <IconLink {...linkWithoutQueryParams(restLink)} key={index + link.link.to}>
              <Icon src={link.icon} />
              {children}
            </IconLink>
          );
        })}
      </Buttons>
    ) : (
      <List css={props.centerLinks && centerListDecorator}>
        <Carousel
          containerDecorator={containerDecorator}
          autoFill
          arrowNavigation
          arrowPosition={-20}
          gap={theme.sharedSpacing.s}
          arrowTheme={Theme.White}
          buttonDecorator={arrowButtonDecorator}
          {...carousel}
        >
          {buttons}
        </Carousel>
      </List>
    );
  },
);

const List = styled(Block, maxContentNormalWidthDecorator, edgeMarginsDecorator, {
  overflow: 'hidden',
});

const centerListDecorator = styled(
  createMaxSmallViewportDecorator({
    maxWidth: '100%',
    width: 'unset',
  }),
);

const arrowButtonDecorator = styled(
  {
    borderRadius: 0,
    height: '100%',
    top: 0,
    border: { xy: { width: 0, color: 'transparent' } },
    boxShadow: '0px 0px 10px 20px white',
    clipPath: 'inset(0px -30px 0px -30px)',
  },
  createButtonHoverDecorator({
    backgroundColor: t => t.white.hover,
    color: t => t.white.text,
  }),
);

const Buttons = styled(
  Block,
  maxContentNormalWidthDecorator,
  edgeMarginsDecorator,
  createMinLargeViewportDecorator({
    flexWrap: 'wrap',
  }),
  {
    display: 'flex',
    overflowX: 'auto',
    padding: {
      bottom: t => (isSvenssons ? t.sharedSpacing.m : t.sharedSpacing.s),
    },
    gap: t => (isSvenssons ? t.sharedSpacing.m : t.sharedSpacing.s),
  },
);

const buttonNoWrapDecorator = styled(createMinLargeViewportDecorator({ padding: { x: 0 } }));

const Button = styled(ButtonLink, {
  fontSize: t => t.fontSize.xs,
  fontWeight: t => (t.id === SiteTheme.NordicNest ? t.fontWeight?.bold : t.fontWeight?.normal),
  whiteSpace: 'nowrap',
  flexShrink: 0,
});

const buttonSizeDecorator = styled(
  { padding: { x: t => t.sharedSpacing.xxl } },
  createMaxSmallViewportDecorator({
    padding: { x: t => t.sharedSpacing.l },
  }),
);

const Base = styled.div({
  display: 'flex',
  justifyContent: 'center',
  flexDirection: 'column',
  alignItems: 'center',
  width: '100%',
});

const Heading = styled.h2(
  createMaxSmallViewportDecorator({
    fontSize: t => t.fontSize.s,
  }),
  {
    marginBottom: t => t.sharedSpacing.m,
    fontFamily: t => t.fontFamily?.secondary,
    textTransform: t => t.heading?.textTransform,
    fontSize: t => (isSvenssons ? t.fontSize.l : t.fontSize.m),
  },
);

const IconLink = styled(
  Link,
  createMaxSmallViewportDecorator({
    padding: { x: t => t.spacing.s },
    ':first-child': {
      paddingLeft: t => t.spacing.m,
    },
  }),
  {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    fontSize: t => t.fontSize.xxs,
    textAlign: 'center',
    ':hover': {
      textDecoration: 'underline',
    },
  },
);

const Icon = styled.img(createMinLargeViewportDecorator({ height: 50, width: 50 }), {
  height: 40,
  width: 40,
  marginBottom: t => t.sharedSpacing.m,
});

const iconListDecorator = styled(
  createMinLargeViewportDecorator({
    justifyContent: 'space-around',
  }),
  createMaxSmallViewportDecorator({
    padding: { x: 0, bottom: t => t.spacing.xs },
  }),
);

const centerButtonsDecorator = styled(
  createMinLargeViewportDecorator({
    justifyContent: 'center',
  }),
);
